<script>
import AjaxLoad from "@/components/commonComponents/AjaxLoad.vue";
import _ from "lodash";
import UserAuth from "@/mixins/UserAuth";
import VFormModified from "@/components/commonComponents/VFormModified.vue";
import VFileInputUpload from "@/components/commonComponents/VFileInputUpload.vue";
import VTextFieldColorPicker from "@/components/commonComponents/VTextFieldColorPicker.vue";
import VCurrencyInput from "@/components/commonComponents/VCurrencyInput.vue";
import Languages from "@/mixins/dictionaries/Languages";

export default {
    name: "ProjectsSettings",
    components: {
        VCurrencyInput,
        VTextFieldColorPicker,
        VFileInputUpload,
        VFormModified,
        AjaxLoad,
    },
    mixins: [
        UserAuth,
        Languages,
    ],
    props: {
        idProject: {
            type: String,
        },
    },
    data() {
        return {
            roles: [
                {
                    id: 'editor',
                    name: 'Контент-менеджмент',
                    multiple: true,
                    hint: 'Пользователи, предлагающие материалы к публикации. Любой из участников группы имеет возможность единолично предложить материал к публикации.'
                },
                {
                    id: 'approver',
                    name: 'Отправка на утверждение',
                    multiple: true,
                    hint: 'Пользователи, отправляющие материалы на утверждение. Отправка на утверждение должна быть явно подтверждена всеми участниками группы.'
                },
                {
                    id: 'moderator',
                    name: 'Утверждение',
                    multiple: true,
                    hint: 'Пользователи, одобряющие публикацию материалов на Портале. Любой из участников группы имеет возможность единолично разрешить публикацию.'
                },
                {
                    id: 'publisher',
                    name: 'Публикация на Портале',
                    multiple: false,
                    hint: 'Пользователь, выполняющий публикацию материалов на Портале.'
                },
                {
                    id: 'observer',
                    name: 'Наблюдатель',
                    multiple: true,
                    hint: 'Пользователи, имеющие read-only доступ к проекту, с полным доступом к комментариям.'
                },
            ],
            original: {},
            project: {},
            projectUsers: [],
            isResolved: false,
            isSubmit: false,
            notifyOptions: {
                title: 'Настройки проекта',
            },
            isValid: false,
            wasModified: false,
        };
    },
    computed: {
        isCanSubmit() {
            return this.isResolved && !this.isSubmit && this.user.permissions.byModule.users2projects.canUpdate;
        },
        isCanSubmitSettings() {
            return this.isCanSubmit && this.wasModified;
        },
        setProjectUsersRolesDebounced() {
            return _.debounce(this.setProjectUsersRoles, 800);
        },
        isLoading() {
            return this.isSubmit || !this.isResolved;
        },
        projectUrl() {
            return this.$apiUrl.getProjectUrl({id_project: this.idProject});
        },
    },
    methods: {
        getUserItem(item) {
            if (item.first_name || item.last_name) {
                return [item.first_name, item.last_name].filter(v => v).join(' ');
            }
            return item.email;
        },
        getProjectUsersByRole(role) {
            return role.multiple ?
                _(this.projectUsers)
                    .filter({role: role.id})
                    .map('id_users')
                    .value() :
                _.find(this.projectUsers, {role: role.id})?.id_users;
        },
        doPutRequest(putData) {
            this.isSubmit = true;
            this.$api.put(
                this.projectUrl,
                putData,
                true
            )
                .then(() => {
                    this.notifySuccess('Успешно сохранены');
                })
                .catch(this.notifyError)
                .finally(() => {
                    this.isSubmit = false;
                    /**
                     * @TODO Это хоть и пытается перечитать список при ошибке, но если и GET запрос возвращает ошибку, то список не меняется...
                     */
                    this.$refs.ajaxProjectUsers.load();
                });
        },
        setProjectUsersRoles(role, id) {
            if (role.multiple) {
                this.projectUsers = _(this.projectUsers)
                    .reject({role: role.id})
                    .concat(id.map(v => ({id_users: v, role: role.id})))
                    .value();
            } else {
                this.projectUsers = _(this.projectUsers)
                    .reject({role: role.id})
                    .concat(id ? [{id_users: id, role: role.id}] : [])
                    .value();
            }

            const putData = {
                projects_users: _(this.projectUsers)
                    .groupBy('role')
                    .mapValues(roles => _.map(roles, 'id_users'))
                    .value(),
            };

            this.doPutRequest(putData);
        },
        transformProjectUsers(response) {
            this.projectUsers = _(response.data.data.users.data)
                .map('projects_users')
                .map(v => ({
                        id_users: v.id_users,
                        role: v.role,
                    })
                )
                .value();

            return _.omit(response.data.data, 'users');
        },
        onClickSaveSettings() {
            this.doPutRequest(this.project);
        },
        // @TODO: mixin
        getSrc(file) {
            return [process.env.VUE_APP_BACKEND_URL, file].join('');
        },
    },
}
</script>

<template>
    <ajax-load
        ref="ajaxProjectUsers"
        :active="!!idProject"
        :url="projectUrl"
        :transform="transformProjectUsers"
        :resolved.sync="isResolved"
        v-model="original"
    >
        <v-card>
            <v-card-title>Пользователи</v-card-title>
            <v-container fluid>
                <v-row
                    v-for="role in roles"
                    :key="role.id"
                    no-gutters
                >
                    <ajax-load
                        :url="$apiUrl.getUsersForProjectRoleUrl({slug: role.id})"
                        :transform="response => response.data.data"
                        v-slot="{data:users}"
                    >
                        <v-col>
                            <v-autocomplete
                                :value="getProjectUsersByRole(role)"
                                :label="role.name"
                                :items="users"
                                :item-text="item => getUserItem(item)"
                                :disabled="!isCanSubmit"
                                :multiple="role.multiple"
                                item-value="id"
                                chips
                                deletable-chips
                                @change="setProjectUsersRolesDebounced(role, $event)"
                                clearable
                                :hint="role.hint"
                                persistent-hint
                            ></v-autocomplete>
                        </v-col>
                    </ajax-load>
                </v-row>
            </v-container>
            <v-card-title>Карточки &laquo;ярких цитат&raquo;, &laquo;облако смыслов&raquo;</v-card-title>
            <v-form-modified
                v-model="isValid"
                :original="original"
                :modified.sync="project"
                :was-modified.sync="wasModified"
                :disabled="isLoading"
            >
                <v-container fluid>
                    <v-row no-gutters>
                        <v-col>
                            <v-container fluid>
                                <v-row no-gutters v-for="language in dictionaries.languages" :key="language.id">
                                    <v-col>
                                        <v-file-input-upload
                                            v-model="project['quotes_background_' + language.id]"
                                            :upload-url="$apiUrl.getProjectQuoteUploadUrl({id_project: idProject, language: language.id})"
                                            :disabled="!isCanSubmit"
                                            accept="image/*"
                                            download
                                            hint="Целевой размер карточки цитаты - 700x700px."
                                            persistent-hint
                                        >
                                            <template v-slot:label>
                                                Фон &laquo;ярких цитат&raquo; ({{language.name}})
                                            </template>
                                        </v-file-input-upload>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <v-currency-input
                                            v-model="project.quotes_background_opacity"
                                            :options="{valueRange: {min: 0, max: 1}, precision: 2}"
                                            :disabled="!isCanSubmit"
                                            label=""
                                        >
                                            <template v-slot:label>
                                                Непрозрачность фона &laquo;ярких цитат&raquo; (opacity)
                                            </template>
                                        </v-currency-input>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <v-text-field-color-picker
                                            v-model="project.quotes_speaker_name_color"
                                            :disabled="!isCanSubmit"
                                            label="Цвет ФИО спикера"
                                        ></v-text-field-color-picker>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <v-text-field-color-picker
                                            v-model="project.quotes_speaker_position_color"
                                            :disabled="!isCanSubmit"
                                            label="Цвет наименования должности спикера"
                                        ></v-text-field-color-picker>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <v-text-field-color-picker
                                            v-model="project.quotes_text_color"
                                            :disabled="!isCanSubmit"
                                            label="Цвет текста цитаты"
                                        ></v-text-field-color-picker>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <v-text-field-color-picker
                                            v-model="project.quotes_ai_color"
                                            :disabled="!isCanSubmit"
                                            label="Цвет текста уведомления об использовании ИИ"
                                        ></v-text-field-color-picker>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                        <v-col>
                            <v-container fluid>
                                <v-row no-gutters v-for="language in dictionaries.languages" :key="language.id">
                                    <v-col>
                                        <v-file-input-upload
                                            v-model="project['wordclouds_background_'+language.id]"
                                            :upload-url="$apiUrl.getProjectWordcloudUploadUrl({id_project: idProject, language: language.id})"
                                            :disabled="!isCanSubmit"
                                            accept="image/*"
                                            download
                                            hint="Целевой размер изображения облака - 700x500px."
                                            persistent-hint
                                        >
                                            <template v-slot:label>
                                                Фон &laquo;облака смыслов&raquo; ({{language.name}})
                                            </template>
                                        </v-file-input-upload>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <v-currency-input
                                            v-model="project.wordclouds_background_opacity"
                                            :options="{valueRange: {min: 0, max: 1}, precision: 2}"
                                            :disabled="!isCanSubmit"
                                        >
                                            <template v-slot:label>
                                                Непрозрачность фона &laquo;облака смыслов&raquo; (opacity)
                                            </template>
                                        </v-currency-input>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters>
                                    <v-col>
                                        <ajax-load
                                            :url="$apiUrl.getProjectDictionaryFontsUrl({id_project: idProject})"
                                            :transform="response => response.data.data"
                                            v-slot="{data:fonts, resolved}"
                                        >
                                            <v-autocomplete
                                                v-model="project.wordclouds_id_fonts"
                                                :items="fonts"
                                                :loading="!resolved"
                                                :disabled="!isCanSubmit"
                                                item-value="id"
                                                item-text="name"
                                                hide-details
                                            >
                                                <template v-slot:label>
                                                    Шрифт &laquo;облака смыслов&raquo;
                                                </template>
                                            </v-autocomplete>
                                        </ajax-load>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form-modified>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    :disabled="!isCanSubmitSettings"
                    @click="onClickSaveSettings"
                    color="primary"
                >
                    Сохранить
                </v-btn>
            </v-card-actions>
        </v-card>
    </ajax-load>
</template>

<style scoped lang="scss">
::v-deep {
    .v-messages__message {
        opacity: 0.7;
    }
}
</style>